import { PortalAccessProfile } from "types/api";

export type PortalSection =
  | "admin_dashboard"
  | "fan_dashboard"
  | "athlete_dashboard"
  | "school_dashboard"
  | "school_onboarding"
  | "school_operations"
  | "sport_management"
  | "team_management"
  | "users"
  | "players"
  | "teams"
  | "sports"
  | "countries"
  | "administrative_regions"
  | "sport_age_groups"
  | "schools"
  | "school_roles"
  | "school_sports"
  | "school_teams"
  | "school_players"
  | "school_calendar"
  | "school_schedule"
  | "school_matches"
  | "school_scoresheets"
  | "school_rosters"
  | "player_performance_records"
  | "events"
  | "news"
  | "merchandise_items"
  | "player_classes"
  | "requests"
  | "orders"
  | "payments"
  | "wallet"
  | "notifications"
  | "followed_athletes"
  | "player_profile"
  | "player_stats";

export type PortalNavItem = {
  key: PortalSection;
  label: string;
  path: string;
  order: number;
  description?: string;
  iconKey: string;
};

export type PortalNavGroupKey =
  | "home"
  | "catalog"
  | "platform"
  | "school"
  | "athlete_tools"
  | "commerce"
  | "community";

export type PortalNavGroup = {
  key: PortalNavGroupKey;
  label: string;
  description?: string;
  order: number;
  items: PortalNavItem[];
};

type PortalSectionConfig = PortalNavItem & {
  slugs: string[];
  matches?: string[];
};

const DASHBOARD_SECTIONS: PortalSection[] = [
  "admin_dashboard",
  "fan_dashboard",
  "athlete_dashboard",
  "school_dashboard",
  "school_onboarding",
  "school_operations",
  "sport_management",
  "team_management",
];

const NAV_GROUP_CONFIG: Record<PortalNavGroupKey, Omit<PortalNavGroup, "items">> = {
  athlete_tools: {
    description: "Profile, requests, classes, events, and athlete-led growth.",
    key: "athlete_tools",
    label: "Athlete Tools",
    order: 6,
  },
  catalog: {
    description: "Sports, teams, athletes, news, and storefront content.",
    key: "catalog",
    label: "Explore",
    order: 2,
  },
  commerce: {
    description: "Orders, payments, and wallet activity.",
    key: "commerce",
    label: "Commerce",
    order: 7,
  },
  community: {
    description: "Inbox and audience-facing activity.",
    key: "community",
    label: "Community",
    order: 8,
  },
  home: {
    description: "Your role-specific control room.",
    key: "home",
    label: "Home",
    order: 0,
  },
  platform: {
    description: "Reference data, setup, and platform-wide administration.",
    key: "platform",
    label: "Setup",
    order: 3,
  },
  school: {
    description:
      "School hub, staff roles, programmes, teams, players, fixtures, documents, and performance.",
    key: "school",
    label: "School Ops",
    order: 4,
  },
};

const PORTAL_SECTION_GROUPS: Partial<Record<PortalSection, PortalNavGroupKey>> = {
  admin_dashboard: "home",
  administrative_regions: "platform",
  athlete_dashboard: "home",
  countries: "platform",
  events: "athlete_tools",
  fan_dashboard: "home",
  followed_athletes: "community",
  merchandise_items: "catalog",
  news: "catalog",
  notifications: "community",
  orders: "commerce",
  payments: "commerce",
  player_classes: "athlete_tools",
  player_profile: "athlete_tools",
  player_stats: "athlete_tools",
  players: "catalog",
  requests: "athlete_tools",
  school_dashboard: "home",
  school_matches: "school",
  school_scoresheets: "school",
  school_schedule: "school",
  school_calendar: "school",
  school_onboarding: "home",
  school_operations: "home",
  school_players: "school",
  school_roles: "school",
  school_rosters: "school",
  school_sports: "school",
  school_teams: "school",
  schools: "school",
  sport_age_groups: "platform",
  sport_management: "home",
  sports: "catalog",
  team_management: "home",
  teams: "catalog",
  users: "platform",
  wallet: "commerce",
  player_performance_records: "school",
};

const PORTAL_SECTION_CONFIG: Record<PortalSection, PortalSectionConfig> = {
  admin_dashboard: {
    key: "admin_dashboard",
    label: "Dashboard",
    path: "/dashboard",
    order: 0,
    iconKey: "dashboard",
    slugs: ["admin-dashboard"],
    matches: ["/dashboard"],
  },
  fan_dashboard: {
    key: "fan_dashboard",
    label: "Dashboard",
    path: "/dashboard",
    order: 0,
    iconKey: "dashboard",
    slugs: ["fan-dashboard"],
    matches: ["/dashboard"],
  },
  athlete_dashboard: {
    key: "athlete_dashboard",
    label: "Dashboard",
    path: "/dashboard",
    order: 0,
    iconKey: "dashboard",
    slugs: ["athlete-dashboard"],
    matches: ["/dashboard"],
  },
  school_dashboard: {
    key: "school_dashboard",
    label: "Dashboard",
    path: "/dashboard",
    order: 0,
    iconKey: "dashboard",
    slugs: ["school-dashboard"],
    matches: ["/dashboard"],
  },
  school_onboarding: {
    key: "school_onboarding",
    label: "Dashboard",
    path: "/dashboard",
    order: 0,
    iconKey: "dashboard",
    slugs: ["school-onboarding"],
    matches: ["/dashboard"],
  },
  school_operations: {
    key: "school_operations",
    label: "Dashboard",
    path: "/dashboard",
    order: 0,
    iconKey: "dashboard",
    slugs: ["school-operations"],
    matches: ["/dashboard"],
  },
  sport_management: {
    key: "sport_management",
    label: "Dashboard",
    path: "/dashboard",
    order: 0,
    iconKey: "dashboard",
    slugs: ["sport-management"],
    matches: ["/dashboard"],
  },
  team_management: {
    key: "team_management",
    label: "Dashboard",
    path: "/dashboard",
    order: 0,
    iconKey: "dashboard",
    slugs: ["team-management"],
    matches: ["/dashboard"],
  },
  users: {
    key: "users",
    label: "Users",
    path: "/users",
    order: 20,
    iconKey: "users",
    slugs: ["users"],
    matches: ["/users"],
  },
  players: {
    key: "players",
    label: "Athletes",
    path: "/athletes",
    order: 12,
    iconKey: "players",
    slugs: ["players"],
    matches: ["/athletes"],
  },
  teams: {
    key: "teams",
    label: "Teams",
    path: "/teams",
    order: 10,
    iconKey: "teams",
    slugs: ["teams"],
    matches: ["/teams"],
  },
  sports: {
    key: "sports",
    label: "Sports",
    path: "/sports",
    order: 8,
    iconKey: "sports",
    slugs: ["sports"],
    matches: ["/sports"],
  },
  countries: {
    key: "countries",
    label: "Countries",
    path: "/countries",
    order: 22,
    iconKey: "countries",
    slugs: ["countries"],
    matches: ["/countries"],
  },
  administrative_regions: {
    key: "administrative_regions",
    label: "Regions",
    path: "/workspace/administrative-regions",
    order: 24,
    iconKey: "regions",
    slugs: ["administrative-regions"],
  },
  sport_age_groups: {
    key: "sport_age_groups",
    label: "Age Groups",
    path: "/workspace/sport-age-groups",
    order: 26,
    iconKey: "age-groups",
    slugs: ["sport-age-groups"],
  },
  schools: {
    key: "schools",
    label: "School",
    path: "/workspace/school",
    order: 28,
    iconKey: "schools",
    slugs: ["school", "schools"],
  },
  school_roles: {
    key: "school_roles",
    label: "Staff Roles",
    path: "/workspace/school-roles",
    order: 30,
    iconKey: "school-roles",
    slugs: ["school-roles"],
  },
  school_sports: {
    key: "school_sports",
    label: "Sport Programmes",
    path: "/workspace/school-sports",
    order: 32,
    iconKey: "school-sports",
    slugs: ["school-sports"],
  },
  school_teams: {
    key: "school_teams",
    label: "Teams",
    path: "/workspace/school-teams",
    order: 34,
    iconKey: "school-teams",
    slugs: ["school-teams"],
  },
  school_players: {
    key: "school_players",
    label: "People",
    path: "/workspace/school-players",
    order: 36,
    iconKey: "school-players",
    slugs: ["school-players"],
  },
  school_matches: {
    key: "school_matches",
    label: "Fixtures & Live",
    path: "/workspace/school-matches",
    order: 38,
    iconKey: "school-matches",
    slugs: ["school-matches"],
  },
  school_schedule: {
    key: "school_schedule",
    label: "Schedule",
    path: "/workspace/schedule",
    order: 37,
    iconKey: "school-calendar",
    slugs: ["schedule"],
  },
  school_scoresheets: {
    key: "school_scoresheets",
    label: "Scoresheets",
    path: "/workspace/school-scoresheets",
    order: 39,
    iconKey: "school-scoresheets",
    slugs: ["school-scoresheets"],
  },
  school_calendar: {
    key: "school_calendar",
    label: "School Calendar",
    path: "/workspace/school-calendar",
    order: 40,
    iconKey: "school-calendar",
    slugs: ["school-calendar"],
  },
  school_rosters: {
    key: "school_rosters",
    label: "Rosters & Team Sheets",
    path: "/workspace/school-rosters",
    order: 41,
    iconKey: "school-rosters",
    slugs: ["school-rosters"],
  },
  player_performance_records: {
    key: "player_performance_records",
    label: "Performance & Highlights",
    path: "/workspace/player-performance-records",
    order: 43,
    iconKey: "performance",
    slugs: ["player-performance-records"],
  },
  events: {
    key: "events",
    label: "Events",
    path: "/workspace/events",
    order: 16,
    iconKey: "events",
    slugs: ["events"],
  },
  news: {
    key: "news",
    label: "News",
    path: "/news",
    order: 18,
    iconKey: "news",
    slugs: ["news"],
    matches: ["/news"],
  },
  merchandise_items: {
    key: "merchandise_items",
    label: "Merchandise",
    path: "/merchandise",
    order: 14,
    iconKey: "merchandise",
    slugs: ["merchandise"],
    matches: ["/merchandise"],
  },
  player_classes: {
    key: "player_classes",
    label: "Classes",
    path: "/workspace/player-classes",
    order: 44,
    iconKey: "classes",
    slugs: ["player-classes"],
  },
  requests: {
    key: "requests",
    label: "Requests",
    path: "/workspace/requests",
    order: 46,
    iconKey: "requests",
    slugs: ["requests"],
  },
  orders: {
    key: "orders",
    label: "Orders",
    path: "/workspace/orders",
    order: 48,
    iconKey: "orders",
    slugs: ["orders"],
  },
  payments: {
    key: "payments",
    label: "Payments",
    path: "/workspace/payments",
    order: 50,
    iconKey: "payments",
    slugs: ["payments"],
  },
  wallet: {
    key: "wallet",
    label: "Wallet",
    path: "/workspace/wallet",
    order: 52,
    iconKey: "wallet",
    slugs: ["wallet"],
  },
  notifications: {
    key: "notifications",
    label: "Notifications",
    path: "/workspace/notifications",
    order: 54,
    iconKey: "notifications",
    slugs: ["notifications"],
  },
  followed_athletes: {
    key: "followed_athletes",
    label: "Followed Athletes",
    path: "/workspace/followed-athletes",
    order: 56,
    iconKey: "followed-athletes",
    slugs: ["followed-athletes"],
  },
  player_profile: {
    key: "player_profile",
    label: "My Athlete Profile",
    path: "/workspace/player-profile",
    order: 58,
    iconKey: "player-profile",
    slugs: ["player-profile"],
  },
  player_stats: {
    key: "player_stats",
    label: "My Stats",
    path: "/workspace/player-stats",
    order: 60,
    iconKey: "player-stats",
    slugs: ["player-stats"],
  },
};

export const formatPortalLabel = (value?: string | null) =>
  String(value ?? "")
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase()) || "User";

export const getPrimaryPortalRole = (
  portalAccess?: PortalAccessProfile | null,
  fallbackRole?: string | null,
) => portalAccess?.primary_role || fallbackRole || "user";

export const getPortalTypeLabel = (
  portalAccess?: PortalAccessProfile | null,
  fallbackRole?: string | null,
) => {
  const portalType = portalAccess?.portal_type;

  if (portalType === "admin") {
    return "Admin Workspace";
  }

  if (portalType === "athlete") {
    return "Athlete Workspace";
  }

  if (portalType === "school_management") {
    return "School Management";
  }

  if (portalType === "fan") {
    return "Fan Workspace";
  }

  return `${formatPortalLabel(getPrimaryPortalRole(portalAccess, fallbackRole))} Workspace`;
};

export const getVisiblePortalSections = (portalAccess?: PortalAccessProfile | null) =>
  portalAccess?.visible_sections || [];

export const hasPortalCapability = (
  portalAccess: PortalAccessProfile | null | undefined,
  capability: keyof NonNullable<PortalAccessProfile["capabilities"]>,
) => Boolean(portalAccess?.capabilities?.[capability]);

export const hasPortalAction = (
  portalAccess: PortalAccessProfile | null | undefined,
  action: string,
) => Boolean(portalAccess?.allowed_actions?.includes(action));

export const isFanPortalAccess = (portalAccess?: PortalAccessProfile | null) =>
  portalAccess?.portal_type === "fan";

export const canManageGlobalAthletes = (portalAccess?: PortalAccessProfile | null) =>
  !isFanPortalAccess(portalAccess) && hasPortalAction(portalAccess, "players.manage");

export const canDeleteGlobalAthletes = (
  portalAccess?: PortalAccessProfile | null,
  role?: string | null,
) =>
  !isFanPortalAccess(portalAccess) &&
  role !== "fan" &&
  (role === "admin" || hasPortalAction(portalAccess, "players.manage"));

export const canManageAllMerchandiseItems = (portalAccess?: PortalAccessProfile | null) =>
  !isFanPortalAccess(portalAccess) &&
  hasPortalAction(portalAccess, "merchandise_items.manage");

export const canManageMerchandiseItems = (
  portalAccess?: PortalAccessProfile | null,
  options?: { hasLinkedPlayer?: boolean },
) => {
  if (isFanPortalAccess(portalAccess)) {
    return false;
  }

  return (
    hasPortalAction(portalAccess, "merchandise_items.manage") ||
    Boolean(options?.hasLinkedPlayer)
  );
};

export const canManageMerchandiseItem = (
  portalAccess?: PortalAccessProfile | null,
  options?: {
    hasLinkedPlayer?: boolean;
    itemPlayerId?: string | number | null;
    linkedPlayerId?: string | number | null;
  },
) => {
  if (isFanPortalAccess(portalAccess)) {
    return false;
  }

  if (hasPortalAction(portalAccess, "merchandise_items.manage")) {
    return true;
  }

  if (!options?.hasLinkedPlayer || options.linkedPlayerId == null || options.itemPlayerId == null) {
    return false;
  }

  return String(options.linkedPlayerId) === String(options.itemPlayerId);
};

export const hasAnyPortalAction = (
  portalAccess: PortalAccessProfile | null | undefined,
  actions: string[],
) => actions.some((action) => hasPortalAction(portalAccess, action));

export const hasLinkedPlayerProfile = (
  currentUser?: { player?: { id?: string | number } | null } | null,
) => Boolean(currentUser?.player?.id);

export const isAthletePortalOwner = (
  currentUser?: { role?: string | null; player?: { id?: string | number } | null } | null,
  portalAccess?: PortalAccessProfile | null,
) =>
  hasLinkedPlayerProfile(currentUser) &&
  (portalAccess?.portal_type === "athlete" || currentUser?.role === "player");

export const canManageOwnedAthleteEvents = (
  currentUser?: { role?: string | null; player?: { id?: string | number } | null } | null,
  portalAccess?: PortalAccessProfile | null,
) =>
  currentUser?.role === "admin" ||
  hasPortalAction(portalAccess, "events.manage") ||
  isAthletePortalOwner(currentUser, portalAccess);

export const canManageOwnedAthleteClasses = (
  currentUser?: { role?: string | null; player?: { id?: string | number } | null } | null,
  portalAccess?: PortalAccessProfile | null,
) =>
  currentUser?.role === "admin" ||
  hasPortalAction(portalAccess, "player_classes.manage") ||
  isAthletePortalOwner(currentUser, portalAccess);

export const canFulfillOwnedAthleteRequests = (
  currentUser?: { role?: string | null; player?: { id?: string | number } | null } | null,
  portalAccess?: PortalAccessProfile | null,
) =>
  currentUser?.role === "admin" ||
  hasPortalAction(portalAccess, "requests.manage") ||
  isAthletePortalOwner(currentUser, portalAccess);

export const getPortalSectionConfig = (section?: string | null) =>
  section ? PORTAL_SECTION_CONFIG[section as PortalSection] || null : null;

export const getDashboardLandingPath = () => "/dashboard";

export const getDefaultPortalPath = (portalAccess?: PortalAccessProfile | null) => {
  const landingSection = portalAccess?.landing_section;
  const config = getPortalSectionConfig(landingSection);

  return config?.path || getDashboardLandingPath();
};

export const getAccessiblePortalPath = (portalAccess?: PortalAccessProfile | null) => {
  if (!portalAccess) {
    return getDashboardLandingPath();
  }

  const preferredPath = getDefaultPortalPath(portalAccess);
  if (canAccessPortalPath(preferredPath, portalAccess)) {
    return preferredPath;
  }

  const accessibleNavItem = getPortalNavItems(portalAccess).find((item) =>
    canAccessPortalPath(item.path, portalAccess),
  );

  if (accessibleNavItem) {
    return accessibleNavItem.path;
  }

  if (canAccessPortalPath(getDashboardLandingPath(), portalAccess)) {
    return getDashboardLandingPath();
  }

  return "/profile";
};

export const getPortalNavGroupKey = (
  section?: PortalSection | null,
  portalAccess?: PortalAccessProfile | null,
): PortalNavGroupKey => {
  if (!section) {
    return "home";
  }

  if (section === "player_performance_records" && portalAccess?.portal_type === "athlete") {
    return "athlete_tools";
  }

  return PORTAL_SECTION_GROUPS[section] || "community";
};

export const getPortalNavItems = (portalAccess?: PortalAccessProfile | null): PortalNavItem[] => {
  const sections = getVisiblePortalSections(portalAccess);
  const dedupedByPath = new Map<string, PortalNavItem>();

  sections.forEach((section) => {
    const config = getPortalSectionConfig(section);

    if (!config) {
      return;
    }

    if (!dedupedByPath.has(config.path)) {
      dedupedByPath.set(config.path, config);
    }
  });

  if (
    !dedupedByPath.has("/dashboard") &&
    sections.some((section) => DASHBOARD_SECTIONS.includes(section as PortalSection))
  ) {
    dedupedByPath.set("/dashboard", PORTAL_SECTION_CONFIG.admin_dashboard);
  }

  const shouldExposeSchoolCalendar =
    sections.includes("school_calendar") ||
    sections.includes("school_matches") ||
    sections.includes("school_scoresheets") ||
    sections.includes("school_rosters") ||
    sections.includes("school_dashboard");

  const shouldExposeScoresheets =
    sections.includes("school_scoresheets") ||
    sections.includes("school_matches") ||
    sections.includes("school_rosters") ||
    sections.includes("school_dashboard");

  const shouldCollapseMatchdayNav =
    sections.includes("school_calendar") ||
    sections.includes("school_matches") ||
    sections.includes("school_scoresheets") ||
    sections.includes("school_rosters") ||
    sections.includes("school_dashboard");

  if (shouldCollapseMatchdayNav && !dedupedByPath.has("/workspace/schedule")) {
    dedupedByPath.set("/workspace/schedule", PORTAL_SECTION_CONFIG.school_schedule);
  } else {
    if (shouldExposeSchoolCalendar && !dedupedByPath.has("/workspace/school-calendar")) {
      dedupedByPath.set("/workspace/school-calendar", PORTAL_SECTION_CONFIG.school_calendar);
    }

    if (shouldExposeScoresheets && !dedupedByPath.has("/workspace/school-scoresheets")) {
      dedupedByPath.set(
        "/workspace/school-scoresheets",
        PORTAL_SECTION_CONFIG.school_scoresheets,
      );
    }
  }

  const shouldCollapseSchoolOrgNav = sections.includes("schools");

  return Array.from(dedupedByPath.values())
    .filter((item) => {
      if (shouldCollapseMatchdayNav) {
        return ![
          "school_calendar",
          "school_matches",
          "school_scoresheets",
          "school_rosters",
        ].includes(item.key);
      }

      return true;
    })
    .filter((item) => {
      if (!shouldCollapseSchoolOrgNav) {
        return true;
      }

      return item.key !== "school_roles" && item.key !== "school_sports";
    })
    .sort((left, right) => left.order - right.order);
};

export const getGroupedPortalNavItems = (
  portalAccess?: PortalAccessProfile | null,
): PortalNavGroup[] => {
  const groupedItems = new Map<PortalNavGroupKey, PortalNavGroup>();

  getPortalNavItems(portalAccess).forEach((item) => {
    const groupKey = getPortalNavGroupKey(item.key, portalAccess);
    const groupConfig = NAV_GROUP_CONFIG[groupKey];

    if (!groupedItems.has(groupKey)) {
      groupedItems.set(groupKey, {
        ...groupConfig,
        items: [],
      });
    }

    groupedItems.get(groupKey)!.items.push(item);
  });

  return Array.from(groupedItems.values())
    .map((group) => ({
      ...group,
      items: [...group.items].sort((left, right) => left.order - right.order),
    }))
    .sort((left, right) => left.order - right.order);
};

export const isSchoolHubWorkspace = (
  section: PortalSection | null,
  portalAccess?: PortalAccessProfile | null,
) => getPortalNavGroupKey(section, portalAccess) === "school";

export const getWorkspaceSiblingTabs = (
  portalAccess: PortalAccessProfile | null | undefined,
  section?: PortalSection | null,
) => {
  if (!section) {
    return {
      group: null,
      tabs: [] as PortalNavItem[],
    };
  }

  const groupKey = getPortalNavGroupKey(section, portalAccess);
  const tabs = getPortalNavItems(portalAccess).filter(
    (item) =>
      item.path.startsWith("/workspace/") &&
      getPortalNavGroupKey(item.key, portalAccess) === groupKey,
  );

  return {
    group: NAV_GROUP_CONFIG[groupKey],
    tabs,
  };
};

export const getPortalSectionFromSlug = (slug?: string | null): PortalSection | null => {
  if (!slug) {
    return null;
  }

  const normalizedSlug = slug.trim().toLowerCase();
  const entry = Object.values(PORTAL_SECTION_CONFIG).find((config) =>
    config.slugs.includes(normalizedSlug),
  );

  return entry?.key || null;
};

export const getPortalSectionForPath = (pathname: string): PortalSection | null => {
  if (pathname === "/profile" || pathname === "/profile/edit") {
    return null;
  }

  if (pathname.startsWith("/workspace/")) {
    const slug = pathname.replace("/workspace/", "").split("/")[0];
    return getPortalSectionFromSlug(slug);
  }

  const entry = Object.values(PORTAL_SECTION_CONFIG).find((config) =>
    (config.matches || [config.path]).some(
      (matchPath) => pathname === matchPath || pathname.startsWith(`${matchPath}/`),
    ),
  );

  return entry?.key || null;
};

export const canAccessPortalPath = (
  pathname: string,
  portalAccess?: PortalAccessProfile | null,
) => {
  if (pathname === "/profile" || pathname === "/profile/edit") {
    return true;
  }

  if (!portalAccess) {
    return pathname === "/dashboard";
  }

  if (portalAccess.portal_type === "admin") {
    return true;
  }

  const section = getPortalSectionForPath(pathname);

  if (!section) {
    return pathname === "/dashboard";
  }

  const visibleSections = getVisiblePortalSections(portalAccess);

  if (DASHBOARD_SECTIONS.includes(section)) {
    return visibleSections.some((visibleSection) =>
      DASHBOARD_SECTIONS.includes(visibleSection as PortalSection),
    );
  }

  if (section === "school_calendar") {
    return (
      visibleSections.includes("school_calendar") ||
      visibleSections.includes("school_scoresheets") ||
      visibleSections.includes("school_matches") ||
      visibleSections.includes("school_rosters") ||
      visibleSections.includes("school_dashboard")
    );
  }

  if (section === "school_scoresheets") {
    return (
      visibleSections.includes("school_scoresheets") ||
      visibleSections.includes("school_matches") ||
      visibleSections.includes("school_rosters") ||
      visibleSections.includes("school_dashboard")
    );
  }

  if (section === "school_matches") {
    return (
      visibleSections.includes("school_matches") ||
      visibleSections.includes("school_scoresheets") ||
      visibleSections.includes("school_rosters") ||
      visibleSections.includes("school_dashboard")
    );
  }

  if (section === "school_schedule") {
    return (
      visibleSections.includes("school_schedule") ||
      visibleSections.includes("school_calendar") ||
      visibleSections.includes("school_scoresheets") ||
      visibleSections.includes("school_matches") ||
      visibleSections.includes("school_rosters") ||
      visibleSections.includes("school_dashboard")
    );
  }

  if (section === "schools") {
    return (
      visibleSections.includes("schools") ||
      visibleSections.includes("school_roles") ||
      visibleSections.includes("school_sports")
    );
  }

  return visibleSections.includes(section);
};

export const getUnauthorizedPortalMessage = (pathname: string) => {
  const section = getPortalSectionForPath(pathname);

  if (
    section === "teams" ||
    section === "users" ||
    section === "countries" ||
    section === "administrative_regions" ||
    section === "sport_age_groups"
  ) {
    return "This workspace is restricted to catalog and platform managers.";
  }

  if (section?.startsWith("school_") || section === "schools") {
    return "This workspace is only available for school-management roles.";
  }

  if (
    section === "player_profile" ||
    section === "player_stats" ||
    section === "player_classes" ||
    section === "player_performance_records"
  ) {
    return "This workspace is only available for athlete-owned accounts.";
  }

  return "You do not have access to this workspace.";
};
